"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { differenceInMinutes, parse } from "date-fns"
import {
  IconClockCheck,
  IconLoader2,
  IconLogin2,
  IconLogout2,
} from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/stores/auth-store"
import { getTodayCheckins, selfCheckin } from "@/lib/hr-attendance-api"
import type { AttendanceTodayState } from "@/lib/hr-types"
import { formatWorkedMinutes, toHHmm } from "./attendance/attendance-utils"

interface CheckinWidgetProps {
  /** Outer offset (margins) — the widget self-gates, so an empty wrapper never
   *  renders when the flag/permission are absent. */
  className?: string
}

/**
 * Compact Apollo check-in band for the dashboard home. SELF-GATES on the
 * `hr_attendance_enabled` flag AND the `hr.attendance.self` permission (read
 * from the auth store) — renders NOTHING otherwise, so it is inert on clinics
 * without the HR attendance module. One primary toggle punches in/out
 * optimistically, and the today-state refetches whenever the tab regains focus
 * (no ticking timer — the worked duration is computed once per render).
 *
 * 260724: additionally gated behind `hr_self_checkin_enabled`, a FRONTEND-ONLY
 * flag that is never sent by the backend (no FEATURE_* env / clinic-profile
 * source wired for it) — so `?? false` below always resolves to hidden. This
 * is a deliberate temporary hide of the punch-in/out UI (product wants it off
 * for now) done without touching the server env, deleting the component, or
 * disabling `FEATURE_HR_ATTENDANCE` itself — the rest of the HR attendance
 * module (admin views, API, backend) stays fully live. Re-enable later by
 * flipping this constant, or by wiring the flag to a real backend source.
 */
export function CheckinWidget({ className }: CheckinWidgetProps) {
  const t = useTranslations("hr.attendance")
  const user = useAuthStore((state) => state.user)
  const enabled = user?.feature_flags?.hr_attendance_enabled ?? false
  const selfCheckinUiEnabled = user?.feature_flags?.hr_self_checkin_enabled ?? false
  const canSelf = user?.permissions?.includes("hr.attendance.self") ?? false

  const [today, setToday] = useState<AttendanceTodayState | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const requestIdRef = useRef(0)

  const load = useCallback(() => {
    const rid = ++requestIdRef.current
    setLoading(true)
    getTodayCheckins()
      .then((data) => {
        if (rid === requestIdRef.current) setToday(data)
      })
      .catch(() => {
        // Silent — the band still offers a punch button on failure.
        if (rid === requestIdRef.current) setToday(null)
      })
      .finally(() => {
        if (rid === requestIdRef.current) setLoading(false)
      })
  }, [])

  // Initial load + refetch on focus (visibilitychange covers tab switches,
  // focus covers window/app switches). Only wired once the widget is live.
  useEffect(() => {
    if (!enabled || !canSelf || !selfCheckinUiEnabled) return
    load()
    const onFocus = () => load()
    const onVisible = () => {
      if (document.visibilityState === "visible") load()
    }
    window.addEventListener("focus", onFocus)
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [enabled, canSelf, selfCheckinUiEnabled, load])

  // Live-ish worked duration — computed once per render from first_in (no timer).
  const workedMinutes = useMemo(() => {
    if (!today) return null
    if (today.status === "in" && today.first_in) {
      const start = parse(today.first_in, "yyyy-MM-dd HH:mm:ss", new Date())
      if (!Number.isNaN(start.getTime())) {
        return Math.max(0, differenceInMinutes(new Date(), start))
      }
    }
    return today.worked_minutes_so_far
  }, [today])

  const handleToggle = useCallback(async () => {
    if (submitting) return
    const prev = today
    const nextDirection: "in" | "out" | undefined = today
      ? today.status === "in"
        ? "out"
        : "in"
      : undefined
    setSubmitting(true)
    // Optimistic status flip (times are corrected from the response).
    if (today && nextDirection) setToday({ ...today, status: nextDirection })
    try {
      const result = await selfCheckin(nextDirection)
      setToday(result)
    } catch (error: unknown) {
      setToday(prev)
      const err = error as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || t("widget.toggleFailed"))
    } finally {
      setSubmitting(false)
    }
  }, [submitting, today, t])

  if (!enabled || !canSelf || !selfCheckinUiEnabled) return null

  const bandCls = cn(
    "flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3 shadow-sm",
    className,
  )

  if (loading && today === null) {
    return (
      <div className={bandCls}>
        <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <IconClockCheck className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>
    )
  }

  const isIn = today?.status === "in"
  const hasPunches = (today?.checkins.length ?? 0) > 0 || today?.first_in != null
  const workedLabel = formatWorkedMinutes(workedMinutes)

  let statusLine: React.ReactNode
  if (isIn && today?.first_in) {
    statusLine = (
      <span className="text-[13.5px]">
        <span className="font-medium text-foreground">
          {t("widget.inSince", { time: toHHmm(today.first_in) })}
        </span>
        {workedLabel && (
          <span className="text-muted-foreground">
            {" "}
            {t("widget.workedSuffix", { worked: workedLabel })}
          </span>
        )}
      </span>
    )
  } else if (hasPunches) {
    statusLine = (
      <span className="text-[13.5px] text-foreground">
        {t("widget.outWorked", { worked: workedLabel ?? "0m" })}
      </span>
    )
  } else {
    statusLine = (
      <span className="text-[13.5px] text-muted-foreground">
        {t("widget.noPunches")}
      </span>
    )
  }

  return (
    <div className={bandCls}>
      <div
        className={cn(
          "grid size-10 shrink-0 place-items-center rounded-lg",
          isIn ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
        )}
      >
        <IconClockCheck className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {t("widget.eyebrow")}
        </div>
        <div className="mt-0.5 truncate">{statusLine}</div>
      </div>
      <Button onClick={handleToggle} disabled={submitting} className="shrink-0">
        {submitting ? (
          <IconLoader2 className="mr-1.5 size-4 animate-spin" />
        ) : isIn ? (
          <IconLogout2 className="mr-1.5 size-4" />
        ) : (
          <IconLogin2 className="mr-1.5 size-4" />
        )}
        {isIn ? t("widget.punchOut") : t("widget.punchIn")}
      </Button>
    </div>
  )
}
